import React from 'react';
import cx from 'classnames';
import { DateTime } from 'luxon';
import { stringUtils } from '~/util';
import { get } from 'lodash';
import { DropDown } from '~/components';
import { MdAssignment, MdAttachment, MdContentCopy } from 'react-icons/md';
import { Link } from 'react-router-dom';
import { prefillFromPTO } from '../FinalTitleOpinion/prefill';
import { useAuth, useRouter } from '~/hooks';
import { useOrderHistory } from './context';
import { duplicateTitleOpinion as duplicateTitleOpinionApi } from '~/api/titleOpinion'; 
import dataFieldsToDuplicate from '~/util/dataFieldsToDuplicate';
import titleOpinionStatus from '~/util/titleOpinionStatus';

export const DateCell = (fieldName) => ({ data }) => {
    const value = data[fieldName];

    if (!value)
        return (<></>);

    const parsed = DateTime.fromISO(value);

    return (
        `${parsed.toLocaleString()}`
    );
};

export const NameCell = ({ firstName, lastName, ...rest }) => {
    const value = `${firstName} ${lastName}`;

    return (
        <span {...rest}>
            {value}
        </span>
    );
};

export const SubmittedCell = ({ data, ...rest }) => {

    const firstName = get(data, 'submittedBy.firstName', '');
    const lastName = get(data, 'submittedBy.lastName', '');

    return (
        <NameCell firstName={firstName} lastName={lastName} {...rest} />
    );
};

export const OwnerCell = ({ data, ...rest }) => {
    return (
        <span {...rest}>
            <OwnerName owners={data.owners} />
        </span>
    );
};

function OwnerName({ owners }) {
    return Array.isArray(owners)
        ? <>{owners.map(mapOwner).filter(o => typeof o == 'string' && o.trim().length > 0).join(', ')}</>
        : <></>;
}

export function mapOwner(owner) {
    if (!owner)
        return '';

    if (owner.name) {
        return `${owner.name}`;
    } else if (owner.entityName) {
        return `${owner.entityName}`;
    } else if (owner.firstName) {
        const { firstName, middleName, lastName } = owner;

        return `${firstName} ${middleName} ${lastName}`;
    }

    let result = [];
    if (owner.primary) {
        result.push(`${owner.primary.firstName} ${owner.primary.middleName} ${owner.primary.lastName}`);
    }
    if (owner.secondary) {
        result.push(`${owner.secondary.firstName} ${owner.secondary.middleName} ${owner.secondary.lastName}`);
    }

    return result.join(', ');
}

const statusLabels = {
    [1]: 'PTO Started',
    [2]: 'PTO Submitted',
    [3]: 'FTO Started',
    [4]: 'FTO Submitted',
    [5]: 'PTO Revision Submitted',
    [6]: 'Canceled',
    [7]: 'STF Started',
    [8]: 'STF Submitted'
};

const StatusPill = ({ status, fallback }) => {
    let label = fallback;

    if (statusLabels[status] != undefined)
        label = statusLabels[status];

    const classes = cx(
        'rounded-full',
        'py-1', 'px-3',
        'text-xs',
        'border',
        {
            ['bg-info-10 text-info-60 border-info-30']: status == 3,
            ['bg-primary-10 text-primary-60 border-primary-30']: [1, 7].includes(status),
            ['bg-success-10 text-success-60 border-success-30']: [2, 4, 8].includes(status),
            //['bg-secondary-10 text-secondary-80 border-secondary-30']: status == 4, 
            ['bg-warning-10 text-warning-60 border-warning-30']: status === 5,
            ['bg-error-10 text-error-60 border-error-30']: status === 6,
        }
    );

    return <span style={{ width: 'max-content', display: 'inline-block' }} className={classes}>{label}</span>;
};

export const StatusCell = ({ data, ...rest }) => {

    return (
        <span {...rest}>
            <StatusPill status={data.statusId} fallback='derp' />
        </span>
    );
};

export const FirmName = (firms = []) => ({ data, ...rest }) => {
    const { firmId } = data;
    const firm = firms.find(f => f.Id == firmId);

    if (firm) {
        return (
            <span {...rest}>
                {stringUtils.truncate(firm.Name)}
            </span>
        );
    }
    return <></>;
};

export const ActionMenu = ({ data, setActiveTitleOpinionId, showRevisionsModal, ...rest }) => {
    const { isLawFirm } = useAuth();
    
    const submitPTO = isLawFirm && !data.hasPTO;
    const viewPTO = data.hasPTO;
    const submitFTO = isLawFirm && !data.hasFTO;
    const viewSFTO = [7, 8].includes(get(data, 'statusId', 0));
    const { history } = useRouter();

    const { setAttachments, showAttachmentsModal } = useOrderHistory();

    const hasDocs = Array.isArray(data.documents) && data.documents.length > 0;

    const canBeRevised = get(data, 'statusId', 0) == 2;

    const duplicatePTO = async(titleOpinionId) => {
        const payload = {
            statusId: titleOpinionStatus.PTO.DRAFTED,
            titleOpinionId_original: titleOpinionId,
            dataToDuplicate: dataFieldsToDuplicate.PTO_DATA_FIELDS_DUPLICATE.dataToDuplicate
        };

        const result = await duplicateTitleOpinionApi(payload);
        if(+result?.id > 0) {
            history.push("/preliminaryopinion/" + result.id);
        } else {
            alert("unable to duplicate PTO");
        }
    };

    const duplicateSTF = async(titleOpinionId) => {
        const payload = {
            statusId: titleOpinionStatus.SFTO.DRAFTED,
            titleOpinionId_original: titleOpinionId,
            dataToDuplicate: dataFieldsToDuplicate.STF_DATA_FIELDS_DUPLICATE.dataToDuplicate
        };

        const result = await duplicateTitleOpinionApi(payload);
        if(+result?.id > 0) {
            history.push("/StraightFinal/" + result.id);
        } else {
            alert("Sorry. Unable to duplicate Straight To Final Opinion");
        }
    };

    return (
        <span {...rest}>
            <DropDown
                wrapperClass="rounded-md border bg-white text-neutral-80 text-sm z-20 filter drop-shadow-xl left-0 top-full mt-1"
                iconClass="text-white"
                iconSize='1.25em'
                buttonClass="p-1 bg-gray-10 text-white focus:outline-none hover:bg-secondary-30 rounded-full flex items-center justify-center"
                text=''>
                <section className="flex flex-col items-stretch w-48 text-gray-80 text-sm">
                    <p className="font-semibold p-4 py-2">Action</p>
                    
                    {viewPTO && (
                        <Link to={`/preliminaryOpinion/${data.id}`} className="py-2 px-4 hover:bg-blue-10 flex items-center">
                            <MdAssignment />
                            <span className="ml-2">View PTO</span>
                        </Link>
                    )}
                    {viewPTO && isLawFirm && (
                        <button
                            className="py-2 px-4 hover:bg-blue-10 flex items-center"
                            onClick={() => duplicatePTO(data.id)}
                        >
                            <MdContentCopy />
                            <span className="ml-2">Duplicate PTO</span>
                        </button>
                    )}
                    {viewSFTO && (
                        <Link to={`/StraightFinal/${data.id}`} className="py-2 px-4 hover:bg-blue-10 flex items-center">
                            <MdAssignment />
                            <span className="ml-2">View STF</span>
                        </Link>
                    )}
                    {viewSFTO && isLawFirm && (
                        <button
                            className="py-2 px-4 hover:bg-blue-10 flex items-center"
                            onClick={() => duplicateSTF(data.id)}
                        >
                            <MdContentCopy />
                            <span className="ml-2">Duplicate STF</span>
                        </button>
                    )}
                    {(submitPTO && !viewSFTO) && (
                        <Link to='/preliminaryOpinion/0' className="py-2 px-4 hover:bg-blue-10 flex items-center">
                            <MdAssignment />
                            <span className="ml-2">Submit PTO</span>
                        </Link>
                    )}
                    {canBeRevised && (
                        <Link to={`/preliminaryOpinion/${data.id}?action=revision`} className="py-2 px-4 hover:bg-blue-10 flex items-center">
                            <MdAssignment />
                            <span className="ml-2">Submit Revision</span>
                        </Link>
                    )}
                    {data.hasPTO && (
                        <button
                            className="py-2 px-4 hover:bg-blue-10 flex items-center"
                            onClick={() => {
                                setActiveTitleOpinionId(data.id);
                                showRevisionsModal();
                            }}
                        >
                            <MdAssignment />
                            <span className="ml-2">View Revisions</span>
                        </button>
                    )}
                    {data.hasFTO && (
                        <Link to={`/finalopinion/${data.id}`} className="py-2 px-4 hover:bg-blue-10 flex items-center">
                            <MdAssignment />
                            <span className="ml-2">View FTO</span>
                        </Link>
                    )}
                    {(submitFTO && !viewSFTO) && (
                        <button
                            className="py-2 px-4 hover:bg-blue-10 flex items-center"
                            onClick={async () => {
                                await prefillFromPTO(data.id);
                                window.scrollTo(0, 0);
                                history.push(`/finalopinion/${data.id}`);
                            }}
                        >
                            <MdAssignment />
                            <span className="ml-2">Submit FTO</span>
                        </button>
                    )}
                    {hasDocs && (
                        <button
                            className='py-2 px-4 hover:bg-blue-10 focus:outline-none flex items-center rounded-b-md'
                            onClick={() => {
                                setAttachments(data.documents);
                                showAttachmentsModal();
                            }}>
                            <MdAttachment className='mr-2' />
                            Attachments
                        </button>
                    )}
                </section>
            </DropDown>
        </span>
    );
};